/**
 * DiagramRenderer - Componente para renderizar diagramas Mermaid y mapas mentales Mind Elixir
 */

import React, { useEffect, useRef, useState } from 'react';
import MindElixir from 'mind-elixir';
import 'mind-elixir/style.css';
import { exportDiagramToImage, copyCodeToClipboard } from './diagramService';

const DownloadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="16" height="16">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
  </svg>
);

const CopyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="16" height="16">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" width="16" height="16">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
  </svg>
);

const ZoomInIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="16" height="16">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM10.5 7.5v6m3-3h-6" />
  </svg>
);

const ZoomOutIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="16" height="16">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM13.5 10.5h-6" />
  </svg>
);

const FullscreenIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="16" height="16">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
  </svg>
);

const CloseFullscreenIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="16" height="16">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
  </svg>
);

const CenterIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="16" height="16">
    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 3.75H6A2.25 2.25 0 003.75 6v1.5M16.5 3.75H18A2.25 2.25 0 0120.25 6v1.5m0 9V18A2.25 2.25 0 0118 20.25h-1.5m-9 0H6A2.25 2.25 0 013.75 18v-1.5M12 9v6m3-3H9" />
  </svg>
);

/**
 * Componente para renderizar diagramas
 * @param {Object} props
 * @param {'mermaid' | 'mindmap'} props.type - Tipo de diagrama
 * @param {string} props.svgContent - Contenido SVG para Mermaid
 * @param {Object} props.mindmapData - Datos JSON para Mind Elixir
 * @param {string} props.code - Código fuente del diagrama
 */
const DiagramRenderer = ({ type, svgContent, mindmapData, code }) => {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const mindmapRef = useRef(null);
  const mindmapContainerRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Renderizar Mind Elixir cuando cambien los datos
  useEffect(() => {
    if (type !== 'mindmap' || !mindmapData || !mindmapContainerRef.current) return;

    let mind = null;
    let observer = null;
    let isInitialized = false;

    // Limpiar contenedor e instancia anterior
    if (mindmapRef.current) {
      try {
        mindmapRef.current.destroy();
      } catch (e) {
        // Ignorar errores de destroy
      }
      mindmapRef.current = null;
    }
    mindmapContainerRef.current.innerHTML = '';

    // Preparar los datos - asegurar que tengan la estructura correcta
    const dataToUse = {
      nodeData: mindmapData.nodeData || mindmapData,
    };
    
    // Asegurar que nodeData tenga id
    if (!dataToUse.nodeData.id) {
      dataToUse.nodeData.id = 'root';
    }

    // Función para inicializar Mind Elixir cuando el contenedor tenga tamaño
    const initMindMap = () => {
      if (isInitialized || !mindmapContainerRef.current) return;

      const rect = mindmapContainerRef.current.getBoundingClientRect();
      
      // Esperar a que el contenedor tenga tamaño real
      if (rect.width <= 0 || rect.height <= 0) {
        requestAnimationFrame(initMindMap);
        return;
      }

      try {
        isInitialized = true;
        
        // Crear instancia de Mind Elixir
        mind = new MindElixir({
          el: mindmapContainerRef.current,
          direction: MindElixir.SIDE,
          draggable: true,
          contextMenu: false,
          toolBar: false,
          nodeMenu: false,
          keypress: false,
          expandable: true, // Habilitar expand/collapse
        });
        
        // Habilitar expand/collapse en nodos con hijos
        mind.bus.addListener('operation', (operation) => {
          if (operation.name === 'addChild' || operation.name === 'removeChild') {
            setTimeout(() => {
              mind.refresh();
              mind.scaleFit();
              mind.toCenter();
            }, 100);
          }
        });
        
        // Inicializar con los datos
        mind.init(dataToUse);
        
        // Asegurar que los nodos con hijos estén expandidos inicialmente
        setTimeout(() => {
          if (mind && mind.nodeData && mind.nodeData.children) {
            const expandAll = (node) => {
              if (node.children && node.children.length > 0) {
                // Marcar nodo como expandido directamente en los datos
                node.expanded = true;
                // Recursivamente expandir hijos
                node.children.forEach(expandAll);
              }
            };
            // Expandir el nodo raíz también
            if (mind.nodeData.children && mind.nodeData.children.length > 0) {
              mind.nodeData.expanded = true;
            }
            mind.nodeData.children.forEach(expandAll);
            mind.refresh();
            mind.scaleFit();
            mind.toCenter();
          }
        }, 300);
        mindmapRef.current = mind;

        // Forzar refresh del layout después de init
        // Mind Elixir v5.10: refresh(), linkDiv(), toCenter(), scaleFit()
        const fitAndCenter = () => {
          if (!mind) return;
          try {
            mind.refresh();
            mind.scaleFit();
            mind.toCenter();
          } catch (e) {
            console.warn('[DiagramRenderer] fitAndCenter error:', e);
          }
        };

        // Esperar a que el chat/modal termine de animarse
        setTimeout(fitAndCenter, 200);
        setTimeout(fitAndCenter, 500);

        // ResizeObserver para manejar cambios de tamaño del contenedor
        observer = new ResizeObserver(() => {
          if (mind) {
            try {
              mind.refresh();
            } catch (e) {}
          }
        });
        observer.observe(mindmapContainerRef.current);

      } catch (error) {
        console.error('[DiagramRenderer] Error inicializando Mind Elixir:', error);
      }
    };

    // Iniciar el proceso de inicialización
    initMindMap();
    
    return () => {
      if (observer) {
        observer.disconnect();
      }
      if (mindmapRef.current) {
        try {
          mindmapRef.current.destroy();
        } catch (e) {
          // Ignorar errores de destroy
        }
        mindmapRef.current = null;
      }
    };
  }, [type, mindmapData]);

  // Renderizar Mermaid SVG
  useEffect(() => {
    if (type === 'mermaid' && svgContent && containerRef.current) {
      containerRef.current.innerHTML = svgContent;
      svgRef.current = containerRef.current.querySelector('svg');
      
      // Ajustar estilos del SVG
      if (svgRef.current) {
        svgRef.current.style.maxWidth = '100%';
        svgRef.current.style.height = 'auto';
        svgRef.current.style.minHeight = '200px';
      }
    }
  }, [type, svgContent]);

  // Aplicar zoom
  useEffect(() => {
    if (svgRef.current) {
      svgRef.current.style.transform = `scale(${zoom})`;
      svgRef.current.style.transformOrigin = 'top left';
    }
  }, [zoom]);

  const handleExportPNG = async () => {
    const filename = type === 'mindmap' ? 'mapa_mental' : 'diagrama';
    
    // Para mindmaps, usar el export nativo de Mind Elixir
    if (type === 'mindmap' && mindmapRef.current) {
      try {
        const blob = await mindmapRef.current.exportPng();
        if (blob) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${filename}_${Date.now()}.png`;
          a.click();
          URL.revokeObjectURL(url);
        }
      } catch (e) {
        console.error('[DiagramRenderer] Error exportando PNG:', e);
      }
      return;
    }
    
    // Para Mermaid, usar el método existente
    if (svgRef.current) {
      await exportDiagramToImage(svgRef.current, `${filename}_${Date.now()}`, 'png');
    }
  };

  const handleExportSVG = async () => {
    const filename = type === 'mindmap' ? 'mapa_mental' : 'diagrama';
    
    // Para mindmaps, usar el export nativo de Mind Elixir
    if (type === 'mindmap' && mindmapRef.current) {
      try {
        const blob = mindmapRef.current.exportSvg();
        if (blob) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${filename}_${Date.now()}.svg`;
          a.click();
          URL.revokeObjectURL(url);
        }
      } catch (e) {
        console.error('[DiagramRenderer] Error exportando SVG:', e);
      }
      return;
    }
    
    // Para Mermaid, usar el método existente
    if (svgRef.current) {
      await exportDiagramToImage(svgRef.current, `${filename}_${Date.now()}`, 'svg');
    }
  };

  const handleCopyCode = async () => {
    if (code) {
      const success = await copyCodeToClipboard(code);
      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.2, 2));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.2, 0.5));
  };

  const handleFullscreen = () => {
    const targetElement = type === 'mindmap' ? mindmapContainerRef.current : containerRef.current;
    if (targetElement) {
      if (!document.fullscreenElement) {
        targetElement.requestFullscreen().then(() => {
          setIsFullscreen(true);
        }).catch(err => console.error('Error al entrar en pantalla completa:', err));
      } else {
        document.exitFullscreen().then(() => {
          setIsFullscreen(false);
        });
      }
    }
  };

  // Escuchar cambios de fullscreen y re-escalar el mindmap
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFull = !!document.fullscreenElement;
      setIsFullscreen(isFull);
      
      // Re-escalar el mindmap cuando cambie fullscreen
      if (type === 'mindmap' && mindmapRef.current) {
        setTimeout(() => {
          try {
            mindmapRef.current.refresh();
            mindmapRef.current.scaleFit();
            mindmapRef.current.toCenter();
          } catch (e) {}
        }, 300);
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [type]);

  
  const handleCenterMap = () => {
    if (mindmapRef.current) {
      mindmapRef.current.scaleFit();
      mindmapRef.current.toCenter();
      setZoom(1);
    }
  };

  return (
    <div className="diagram-renderer">
      {/* Barra de herramientas para Mermaid */}
      {type === 'mermaid' && (
        <div className="diagram-toolbar">
          <div className="diagram-toolbar-left">
            <button 
              className="diagram-btn" 
              onClick={handleZoomOut}
              title="Reducir"
            >
              <ZoomOutIcon />
            </button>
            <span className="diagram-zoom-level">{Math.round(zoom * 100)}%</span>
            <button 
              className="diagram-btn" 
              onClick={handleZoomIn}
              title="Ampliar"
            >
              <ZoomInIcon />
            </button>
            <button 
              className="diagram-btn" 
              onClick={handleFullscreen}
              title={isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
            >
              {isFullscreen ? <CloseFullscreenIcon /> : <FullscreenIcon />}
            </button>
          </div>
          
          <div className="diagram-toolbar-right">
            <button 
              className="diagram-btn diagram-btn-primary" 
              onClick={handleExportPNG}
              title="Descargar PNG"
            >
              <DownloadIcon />
              <span>PNG</span>
            </button>
            <button 
              className="diagram-btn" 
              onClick={handleExportSVG}
              title="Descargar SVG"
            >
              <DownloadIcon />
              <span>SVG</span>
            </button>
          </div>
        </div>
      )}
      
      {/* Contenedor para Mermaid */}
      {type === 'mermaid' && (
        <div 
          className="diagram-container"
          ref={containerRef}
          style={{ 
            overflow: 'auto',
            maxHeight: '400px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '16px'
          }}
        />
      )}

      {/* Contenedor para Mind Elixir */}
      {type === 'mindmap' && (
        <>
          <div 
            className="mindmap-container"
            ref={mindmapContainerRef}
            style={{ 
              width: '100%',
              height: '400px',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              overflow: 'hidden'
            }}
          />
          <div className="diagram-toolbar" style={{ marginTop: '8px' }}>
            <div className="diagram-toolbar-left">
              <button 
                className="diagram-btn" 
                onClick={handleCenterMap}
                title="Centrar mapa"
              >
                <CenterIcon />
              </button>
              <button 
                className="diagram-btn" 
                onClick={handleFullscreen}
                title={isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
              >
                {isFullscreen ? <CloseFullscreenIcon /> : <FullscreenIcon />}
              </button>
            </div>
            <div className="diagram-toolbar-right">
              <button 
                className="diagram-btn diagram-btn-primary" 
                onClick={handleExportPNG}
                title="Descargar PNG"
              >
                <DownloadIcon />
                <span>PNG</span>
              </button>
              <button 
                className="diagram-btn" 
                onClick={handleExportSVG}
                title="Descargar SVG"
              >
                <DownloadIcon />
                <span>SVG</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DiagramRenderer;
